import * as XLSX from 'xlsx';
import React, { useState, useEffect, useCallback } from 'react';
import { can } from '../utils/permissions';
import Footer from './common/Footer';
import Sidebar from './Sidebar';
import financeService from '../services/financeService';

/* ───── STATUS BADGE ───── */
const StatusBadge = ({ status }) => {
  const map = {
    paid:      { bg: '#D4EDDA', color: '#155724', label: 'Paid' },
    pending:   { bg: '#FFF3CD', color: '#856404', label: 'Pending' },
    overdue:   { bg: '#F8D7DA', color: '#721C24', label: 'Overdue' },
    active:    { bg: '#D4EDDA', color: '#155724', label: 'Active' },
    low:       { bg: '#FFF3CD', color: '#856404', label: 'Low Balance' },
    blocked:   { bg: '#F8D7DA', color: '#721C24', label: 'Blocked' },
    topup:     { bg: '#D4EDDA', color: '#155724', label: 'Top-up' },
    expense:   { bg: '#F8D7DA', color: '#721C24', label: 'Expense' },
    'not due': { bg: '#FFF3CD', color: '#856404', label: 'Not Due' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#E8E4DF', color: '#6B6B6B', label: status };
  return (
    <span style={{ padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

const Th = ({ children }) => (
  <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'white', background: '#1B3B5F' }}>{children}</th>
);

const EmptyRow = ({ cols, msg }) => (
  <tr><td colSpan={cols} style={{ padding: '50px 20px', textAlign: 'center', color: '#6B6B6B', fontSize: 15 }}>{msg}</td></tr>
);

const LoadingBox = ({ msg = 'Loading…' }) => (
  <div style={{ padding: '50px', textAlign: 'center', color: '#6B6B6B', fontSize: 15 }}>{msg}</div>
);


/* ───── PAGINATION (matches Admission screen style) ───── */
const Pagination = ({ page, total, perPage, onPage, onPerPage }) => {
  const totalPages = Math.ceil(total / perPage);
  if (total === 0) return null;
  const start = page * perPage + 1;
  const end   = Math.min((page + 1) * perPage, total);
  const pages = Array.from({ length: totalPages }, (_, i) => i);
  const Btn = ({ label, onClick, disabled, active }) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'7px 11px', border: active ? 'none' : '1px solid #E8E4DF', borderRadius: 4, minWidth: 32,
      background: active ? '#1B3B5F' : disabled ? '#F8F6F3' : '#fff',
      color: active ? '#fff' : disabled ? '#ccc' : '#2C2C2C',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: active ? 700 : 400,
    }}>{label}</button>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 40px', borderTop:'1px solid #E8E4DF', background:'#fff' }}>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <Btn label="«" onClick={()=>onPage(0)} disabled={page===0}/>
        <Btn label="‹" onClick={()=>onPage(page-1)} disabled={page===0}/>
        {pages.map(i => <Btn key={i} label={i+1} onClick={()=>onPage(i)} active={i===page}/>)}
        <Btn label="›" onClick={()=>onPage(page+1)} disabled={page>=totalPages-1}/>
        <Btn label="»" onClick={()=>onPage(totalPages-1)} disabled={page>=totalPages-1}/>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#6B6B6B' }}>
        <span>Showing {start}–{end} of {total} &nbsp;|&nbsp; Per page</span>
        {[10,20,30,50].map(s => (
          <button key={s} onClick={()=>{ if(onPerPage) { onPerPage(s); onPage(0); } }} style={{
            padding:'5px 11px', border:'none', borderRadius:4, cursor:'pointer', fontSize:13, fontWeight:600,
            background: perPage===s ? '#D4AF37' : '#F0EDE8', color: perPage===s ? '#fff' : '#888',
          }}>{s}</button>
        ))}
      </div>
    </div>
  );
};

/* ───── MAIN COMPONENT ───── */
const FinanceScreen = ({ onNavigate, onLogout }) => {
  const [activeTab,    setActiveTab]    = useState('fees');
  const resetFinancePages = () => { setFeePage(0); setPcPage(0); setTxnPage(0); setOutPage(0); };
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterType,   setFilterType]   = useState('daily');
  const [filterDate,   setFilterDate]   = useState(new Date().toISOString().split('T')[0]);
  const [filterMonth,  setFilterMonth]  = useState(new Date().toISOString().slice(0, 7));

  const [overview,    setOverview]    = useState(null);
  const [feeRows,     setFeeRows]     = useState([]);
  const [paycardRows, setPaycardRows] = useState([]);
  const [txnRows,     setTxnRows]     = useState([]);
  const [outstanding, setOutstanding] = useState([]);

  const [loadOv,   setLoadOv]   = useState(true);
  const [perPage,   setPerPage]   = useState(10);
  const [feePage,   setFeePage]   = useState(0);
  const [pcPage,    setPcPage]    = useState(0);
  const [txnPage,   setTxnPage]   = useState(0);
  const [outPage,   setOutPage]   = useState(0);
  const [loadFee,  setLoadFee]  = useState(false);
  const [loadPC,   setLoadPC]   = useState(false);
  const [loadTxn,  setLoadTxn]  = useState(false);
  const [loadOut,  setLoadOut]  = useState(false);
  const [err,      setErr]      = useState('');

  const selStyle = { padding: '12px 15px', border: '2px solid #E8E4DF', borderRadius: 6, fontSize: 14, fontFamily: 'Work Sans, sans-serif', fontWeight: 600, cursor: 'pointer', outline: 'none' };

  /* overview — load once */
  useEffect(() => {
    financeService.getOverview()
      .then(setOverview).catch(() => setErr('Could not load overview'))
      .finally(() => setLoadOv(false));
  }, []);

  /* fee list — reload when tab, search or statusFilter changes */
  useEffect(() => {
    if (activeTab !== 'fees') return;
    setLoadFee(true);
    financeService.getFeeList(statusFilter, search)
      .then(setFeeRows).catch(() => setErr('Could not load fees'))
      .finally(() => setLoadFee(false));
  }, [activeTab, statusFilter, search]);

  /* paycard list */
  useEffect(() => {
    if (activeTab !== 'paycard') return;
    setLoadPC(true);
    financeService.getPayCardList(search)
      .then(setPaycardRows).catch(() => setErr('Could not load pay cards'))
      .finally(() => setLoadPC(false));
  }, [activeTab, search]);

  /* outstanding */
  useEffect(() => {
    if (activeTab !== 'outstanding') return;
    setLoadOut(true);
    financeService.getOutstanding()
      .then(setOutstanding).catch(() => setErr('Could not load outstanding dues'))
      .finally(() => setLoadOut(false));
  }, [activeTab]);

  const loadTxns = useCallback(() => {
    setLoadTxn(true);
    financeService.getTransactions(filterType, filterDate, filterMonth)
      .then(setTxnRows).catch(() => setErr('Could not load transactions'))
      .finally(() => setLoadTxn(false));
  }, [filterType, filterDate, filterMonth]);

  useEffect(() => { if (activeTab === 'transactions') loadTxns(); }, [activeTab]); // eslint-disable-line

  const rowHover = (e, on) => { e.currentTarget.style.background = on ? '#F8F6F3' : 'transparent'; };

  const oCards = overview ? [
    { icon: '💰', label: 'Total Collection',  value: overview.totalCollectionFmt,     sub: 'All fee payments collected',          accent: '#2D5F4F' },
    { icon: '⏳', label: 'Pending Payments',  value: overview.pendingPaymentsFmt,     sub: `${overview.pendingCount} Students`,   accent: '#F59E0B' },
    { icon: '💳', label: 'Pay Card Balance',  value: overview.totalPaycardBalanceFmt, sub: 'Total active balance',                accent: '#D4AF37' },
    { icon: '⚠️', label: 'Overdue',           value: overview.overdueAmountFmt,       sub: `${overview.overdueCount} Students`,   accent: '#DC3545' },
  ] : Array(4).fill({ icon: '…', label: '—', value: '—', sub: 'Loading…', accent: '#C4BEB8' });

  const tabs = [
    can('FINANCE_FULL') || can('FINANCE_READ') ? { id: 'fees',         label: 'Admission Fees'        } : null,
    can('FINANCE_FULL') || can('FINANCE_READ') ? { id: 'paycard',      label: 'Pay Card'              } : null,
    can('FINANCE_FULL') || can('FINANCE_READ') ? { id: 'transactions', label: 'Pay Card Transactions' } : null,
    can('ACCOUNTING')                          ? { id: 'outstanding',  label: 'Outstanding Dues'      } : null,
  ].filter(Boolean);

  const totalOuts = outstanding.reduce((s, r) => s + (r.balance || 0), 0);


  // ── Excel Export ──────────────────────────────────────────────────────────
  const downloadExcel = () => {
    let data = [];
    let sheetName = 'Finance';
    let fileName = 'finance';

    if (activeTab === 'fees') {
      sheetName = 'Admission Fees';
      fileName = 'admission_fees';
      data = feeRows.map(r => ({
        'Admission No':   r.admissionNumber || '',
        'Student Name':   r.studentName     || '',
         'Total Fees':     r.totalFees       || 0,
        'Amount Paid':    r.paidAmount      || 0,
        'Balance Due':    r.balance         || 0,
        'Status':         r.status          || '',
      }));
    } else if (activeTab === 'paycard') {
      sheetName = 'Pay Cards';
      fileName = 'pay_cards';
      data = paycardRows.map(r => ({
        'Student Name':   r.studentName     || '',
        'Card No':        r.cardNumber      || '',
        'Balance':        r.balance         || 0,
        'Status':         r.status          || '',
      }));
    } else if (activeTab === 'outstanding') {
      sheetName = 'Outstanding';
      fileName = 'outstanding_dues';
       data = outstanding.map(r => ({
        'Admission No':   r.admissionNumber || '',
        'Student Name':   r.studentName     || '',
        'Room':           r.roomCode        || '',
        'Total Fees':     r.totalFees       || 0,
        'Amount Paid':    r.paidAmount      || 0,
        'Balance Due':    r.balance         || 0,
        'Status':         r.status          || '',
      }));
    } else if (activeTab === 'transactions') {
      sheetName = 'Transactions';
      fileName = 'transactions';
      data = txnRows.map(r => ({
        'Date':           r.date            || '',
        'Student Name':   r.studentName     || '',
        'Amount':         r.amount          || 0,
        'Type':           r.type            || '',
        'Payment Mode':   r.paymentMode     || '',
        'Reference':      r.reference       || '',
      }));
    }

    if (data.length === 0) { alert('No data to export.'); return; }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="finance" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content" style={{ background: '#F8F6F3' }}>

        {/* TOP BAR */}
        <div style={{ background: 'white', padding: '25px 40px', borderBottom: '1px solid #E8E4DF' }}>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 400, color: '#1B3B5F', margin: 0 }}>Finance Management</h1>
        </div>

        <div style={{ padding: 40 }}>

          {err && <div style={{ background: '#F8D7DA', border: '1px solid #F5C6CB', borderRadius: 8, padding: '12px 20px', marginBottom: 20, color: '#721C24', fontSize: 14 }}>⚠️ {err}</div>}

          {/* OVERVIEW CARDS */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 25, marginBottom: 40 }}>
            {oCards.map((c, i) => (
              <div key={i} style={{ background: 'white', padding: 30, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: c.accent }} />
                <div style={{ width: 50, height: 50, borderRadius: 12, background: '#F8F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 15 }}>{c.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B', marginBottom: 10 }}>{c.label}</div>
                <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: '#1B3B5F', marginBottom: 5 }}>{loadOv ? '—' : c.value}</div>
                <div style={{ fontSize: 13, color: '#6B6B6B' }}>{loadOv ? 'Loading…' : c.sub}</div>
              </div>
            ))}
          </div>

          {/* SEARCH & FILTER BAR */}
          <div style={{ background: 'white', padding: '20px 25px', borderRadius: 12, marginBottom: 30, display: 'flex', gap: 15, alignItems: 'center', flexWrap: 'wrap', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ flex: 1, minWidth: 300, position: 'relative' }}>
              <input
                style={{ width: '100%', padding: '12px 45px 12px 15px', border: '2px solid #E8E4DF', borderRadius: 6, fontSize: 14, fontFamily: 'Work Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }}
                placeholder="Search by Student ID, Name, or Room Number..."
                value={search} onChange={e => setSearch(e.target.value)}
                onFocus={e => e.target.style.borderColor = '#1B3B5F'}
                onBlur={e => e.target.style.borderColor = '#E8E4DF'}
              />
              <span style={{ position: 'absolute', right: 15, top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B', fontSize: 18 }}>🔍</span>
            </div>
            {(activeTab === 'fees' || activeTab === 'outstanding') && (
              <select style={{ ...selStyle, minWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
              </select>
            )}
            <button onClick={() => { setSearch(''); setStatusFilter('all'); }}
              style={{ padding: '12px 20px', background: '#E8E4DF', color: '#2C2C2C', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={e => e.target.style.background = '#C4BEB8'} onMouseLeave={e => e.target.style.background = '#E8E4DF'}>
              Clear Filters
            </button>
          </div>

          {/* TABS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 30 }}>
            <div style={{ display: 'flex', borderBottom: '2px solid #E8E4DF', flex: 1 }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => { setErr(''); setActiveTab(t.id); }}
                  style={{ padding: '15px 30px', background: 'transparent', border: 'none', borderBottom: `3px solid ${activeTab === t.id ? '#D4AF37' : 'transparent'}`, fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: activeTab === t.id ? '#1B3B5F' : '#6B6B6B', cursor: 'pointer', marginBottom: -2, fontFamily: 'Work Sans, sans-serif' }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ paddingBottom: 17 }}>
              <button onClick={downloadExcel} style={{ padding: '12px 25px', background: '#2D5F4F', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                onMouseEnter={e => { e.currentTarget.style.background = '#25503F'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#2D5F4F'; e.currentTarget.style.transform = 'none'; }}>
                <span>📥</span> Download Excel
              </button>
            </div>
          </div>

          {/* TAB: ADMISSION FEES */}
          {activeTab === 'fees' && (
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              {loadFee ? <LoadingBox msg="Loading fee data…" /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><Th>Student ID</Th><Th>Student Name</Th><Th>Total Fees</Th><Th>Paid Amount</Th><Th>Balance</Th><Th>Status</Th><Th>Action</Th></tr></thead>
                  <tbody>
                    {feeRows.length === 0 ? <EmptyRow cols={7} msg="No admission fee records found." /> : feeRows.slice(feePage*perPage, (feePage+1)*perPage).map((r, i) => (
                      <tr key={i} onMouseEnter={e => rowHover(e, true)} onMouseLeave={e => rowHover(e, false)}>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, fontWeight: 600 }}>{r.admissionNumber}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.studentName}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.totalFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#2D5F4F', fontWeight: 700 }}>{r.paidFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: r.status !== 'paid' ? '#DC3545' : '#2D5F4F', fontWeight: 700 }}>{r.balanceFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}><StatusBadge status={r.status} /></td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}>
                          <button onClick={() => onNavigate('studentFeeDetails', { feeStudentId: r.admissionId })}
                            style={{ padding: '8px 16px', background: '#1B3B5F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            onMouseEnter={e => e.target.style.background = '#0D1F2D'} onMouseLeave={e => e.target.style.background = '#1B3B5F'}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination page={feePage} total={feeRows.length} perPage={perPage} onPage={setFeePage} onPerPage={setPerPage}/>
            </div>
          )}

          {/* TAB: PAY CARD */}
          {activeTab === 'paycard' && (
            <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              {loadPC ? <LoadingBox msg="Loading pay card data…" /> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><Th>Student ID</Th><Th>Student Name</Th><Th>Current Balance</Th><Th>Total Top-up</Th><Th>Total Spent</Th><Th>Status</Th><Th>Action</Th></tr></thead>
                  <tbody>
                    {paycardRows.length === 0
                      ? <EmptyRow cols={7} msg="No pay cards found. Cards are created automatically on first top-up." />
                      : paycardRows.slice(pcPage*perPage, (pcPage+1)*perPage).map((r, i) => (
                      <tr key={i} onMouseEnter={e => rowHover(e, true)} onMouseLeave={e => rowHover(e, false)}>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, fontWeight: 600 }}>{r.admissionNumber}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.studentName}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: r.balance < 2000 ? '#DC3545' : '#2D5F4F', fontWeight: 700 }}>{r.balanceFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.topupFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.spentFmt}</td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}><StatusBadge status={r.status} /></td>
                        <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}>
                          <button onClick={() => onNavigate('payCardDetails', { payCardStudentId: r.admissionId })}
                            style={{ padding: '8px 16px', background: '#D4AF37', color: '#1B3B5F', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            onMouseEnter={e => e.target.style.background = '#C19A2E'} onMouseLeave={e => e.target.style.background = '#D4AF37'}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination page={pcPage} total={paycardRows.length} perPage={perPage} onPage={setPcPage} onPerPage={setPerPage}/>
            </div>
          )}

          {/* TAB: TRANSACTIONS */}
          {activeTab === 'transactions' && (
            <>
              <div style={{ background: 'white', padding: '20px 25px', borderRadius: 12, marginBottom: 25, display: 'flex', gap: 20, alignItems: 'flex-end', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>Filter Type</label>
                  <select style={{ ...selStyle, minWidth: 180 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
                    <option value="daily">Today</option>
                    <option value="date">Specific Date</option>
                    <option value="month">Monthly</option>
                  </select>
                </div>
                {filterType !== 'month' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>Select Date</label>
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
                      style={{ padding: '10px 15px', border: '2px solid #E8E4DF', borderRadius: 6, fontSize: 14, fontFamily: 'Work Sans, sans-serif', minWidth: 180, outline: 'none' }} />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6B6B6B' }}>Select Month</label>
                    <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
                      style={{ padding: '10px 15px', border: '2px solid #E8E4DF', borderRadius: 6, fontSize: 14, fontFamily: 'Work Sans, sans-serif', minWidth: 180, outline: 'none' }} />
                  </div>
                )}
                <button onClick={loadTxns}
                  style={{ padding: '10px 20px', background: '#1B3B5F', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}
                  onMouseEnter={e => e.target.style.background = '#0D1F2D'} onMouseLeave={e => e.target.style.background = '#1B3B5F'}>
                  Apply Filter
                </button>
              </div>
              <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {loadTxn ? <LoadingBox msg="Loading transactions…" /> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><Th>Date & Time</Th><Th>Student ID</Th><Th>Student Name</Th><Th>Type</Th><Th>Category</Th><Th>Amount</Th><Th>Balance After</Th><Th>Description</Th></tr></thead>
                    <tbody>
                      {txnRows.length === 0
                        ? <EmptyRow cols={8} msg="No transactions found for the selected period." />
                        : txnRows.slice(txnPage*perPage, (txnPage+1)*perPage).map((r, i) => (
                        <tr key={i} onMouseEnter={e => rowHover(e, true)} onMouseLeave={e => rowHover(e, false)}>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.dateTime}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, fontWeight: 600 }}>{r.admissionNumber}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.studentName}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}><StatusBadge status={r.type?.toLowerCase()} /></td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.category}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, fontWeight: 700, color: r.type === 'TOPUP' ? '#2D5F4F' : '#DC3545' }}>
                            {r.type === 'TOPUP' ? '+' : '-'}₹{Number(r.amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>₹{Number(r.balanceAfter || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#6B6B6B' }}>{r.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              <Pagination page={txnPage} total={txnRows.length} perPage={perPage} onPage={setTxnPage} onPerPage={setPerPage}/>
              </div>
            </>
          )}

          {/* TAB: OUTSTANDING DUES */}
          {activeTab === 'outstanding' && (
            <>
              <div style={{ background: '#FFF3CD', border: '2px solid #F59E0B', borderRadius: 8, padding: '20px 25px', marginBottom: 25, display: 'flex', alignItems: 'center', gap: 15 }}>
                <div style={{ fontSize: 32 }}>⚠️</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 5 }}>Outstanding Dues Summary</div>
                  <div style={{ fontSize: 14, color: '#6B6B6B' }}>
                    Total pending: <strong style={{ color: '#DC3545' }}>₹{totalOuts.toLocaleString('en-IN')}</strong> across <strong>{outstanding.length} students</strong>
                  </div>
                </div>
              </div>
              <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {loadOut ? <LoadingBox msg="Loading outstanding dues…" /> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><Th>Student ID</Th><Th>Student Name</Th><Th>Room</Th><Th>Total Fees</Th><Th>Paid Amount</Th><Th>Balance</Th><Th>Valid Till</Th><Th>Status</Th><Th>Action</Th></tr></thead>
                    <tbody>
                      {outstanding.length === 0
                        ? <EmptyRow cols={9} msg="✅ No outstanding dues. All students are up to date!" />
                        : outstanding.slice(outPage*perPage, (outPage+1)*perPage).map((r, i) => (
                        <tr key={i}
                          style={{ background: r.status === 'overdue' ? '#FFFBF0' : 'white' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8F6F3'}
                          onMouseLeave={e => e.currentTarget.style.background = r.status === 'overdue' ? '#FFFBF0' : 'white'}>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, fontWeight: 600 }}>{r.admissionNumber}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.studentName}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.roomCode || '—'}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.totalFmt}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#2D5F4F', fontWeight: 700 }}>{r.paidFmt}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14, color: '#DC3545', fontWeight: 700 }}>{r.balanceFmt}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF', fontSize: 14 }}>{r.validTill || '—'}</td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}><StatusBadge status={r.status} /></td>
                          <td style={{ padding: '18px 20px', borderBottom: '1px solid #E8E4DF' }}>
                            <button onClick={() => onNavigate('studentFeeDetails', { feeStudentId: r.admissionId })}
                              style={{ padding: '8px 16px', background: '#1B3B5F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                              onMouseEnter={e => e.target.style.background = '#0D1F2D'} onMouseLeave={e => e.target.style.background = '#1B3B5F'}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              <Pagination page={outPage} total={outstanding.length} perPage={perPage} onPage={setOutPage} onPerPage={setPerPage}/>
              </div>
            </>
          )}

        </div>
        <Footer />
      </main>
    </div>
  );
};

export default FinanceScreen;